import moment from 'moment';
import React from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { FormatOptionLabelMeta } from 'react-select';
import { ReportMarketType, ReportType } from '~/components/Admin';
import { BSModal } from '~/components/Bootstrap';
import { DatePicker, Form, FormGroup, ValidationMessage } from '~/components/Form';
import { OptionWithDescription, Select } from '~/components/Select';
import { ServicesContext, useAdminService } from '~/services';
import { enumValue } from '~/util';

interface ReportTypeType {
    value: number;
    label: string;
    description: string;
}
interface ReportProviderType {
    label: string;
    value: number;
    enabled: boolean;
}

const reportOptions: ReportTypeType[] = [
    {
        value: enumValue(['report', 'type', 'User Access']) as number,
        label: 'User Access',
        description: 'A report that shows user access, status, and last login for all users',
    },
    {
        value: enumValue(['report', 'type', 'User Activity']) as number,
        label: 'User Activity',
        description: 'A report that shows all activity for a specific user',
    },
    {
        value: enumValue(['report', 'type', 'User Profile']) as number,
        label: 'User Profile',
        description: `A report that shows all changes associated with a specific user's profile`,
    },
    {
        value: enumValue(['report', 'type', 'Patient Activity']) as number,
        label: 'Patient Activity',
        description: 'A report that shows all user activity associated with a specific patient',
    },
];

interface CreateReportProps {
    marketList: ReportMarketType[];
}
export function CreateReport({ marketList }: CreateReportProps) {
    const servicesContext = React.useContext(ServicesContext);
    const { getUsers } = useAdminService();
    const { createReport } = useAdminService();
    const [show, setShow] = React.useState(false);
    const [valid, setValid] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [reportType, setReportType] = React.useState<null | ReportTypeType>(null);
    const [marketRestrictions, setMarketRestrictions] = React.useState<ReportMarketType[]>([]);
    const [selectedUser, setSelectedUser] = React.useState<null | { value: number; label: string }>(
        null
    );
    const [patientIds, setPatientIds] = React.useState('');
    const [selectedProvider, setSelectedProvider] = React.useState<null | ReportProviderType>(null);
    const [patientSelectorDisabled, setPatientSelectorDisabled] = React.useState<null | string>(
        null
    );
    const [startDate, setStartDate] = React.useState<null | Date>(null);
    const [endDate, setEndDate] = React.useState<null | Date>(null);
    const [invalidDatesError, setInvalidDatesError] = React.useState('');
    const [validationErrors, setValidationErrors] = React.useState('');

    const isUserAccessReport = reportType?.value === enumValue(['report', 'type', 'User Access']);

    function clearInputs() {
        setReportType(null);
        setMarketRestrictions([]);
        setSelectedUser(null);
        setStartDate(null);
        setEndDate(null);
        setInvalidDatesError('');
        setValidationErrors('');
        setSelectedProvider(null);
        setPatientIds('');
    }

    function close() {
        clearInputs();
        setShow(false);
    }

    async function save() {
        try {
            setSaving(true);
            const data: { type: number; filters: ReportType['filters'] } = {
                type: reportType!.value,
                filters: { summary: [] },
            };
            if (isUserAccessReport) {
                data.filters.markets = marketRestrictions.map((x) => x.value);
                data.filters.summary = marketRestrictions.map((x) => x.label);
            } else {
                data.filters.from = moment(startDate).format('YYYY-MM-DD');
                data.filters.to = moment(endDate).format('YYYY-MM-DD');
                if (reportType!.label === 'Patient Activity') {
                    if (patientIds !== '') {
                        data.filters.patient_gmpis = patientIds
                            .split(',')
                            .map((x) => parseInt(x.trim()));
                    } else {
                        data.filters.provider_id = selectedProvider!.value;
                        data.filters.summary.push(selectedProvider!.label);
                    }
                } else {
                    data.filters.user_id = selectedUser!.value;
                    data.filters.summary = [selectedUser!.label];
                }
            }
            await createReport(data);
            setSaving(false);
            close();
        } catch (e) {
            if (Array.isArray(e.response)) {
                setValidationErrors(
                    e.response.map((x: { message: string }) => x?.message).join(', ')
                );
            }
            setSaving(false);
        }
    }

    async function searchProviders(val: string) {
        const results = await servicesContext.api.call({
            method: 'get',
            url: `/api/providers?name=${val}`,
        });
        // eslint-disable-next-line babel/camelcase
        return results.data.map((x: { id: number; name: string; is_enabled: boolean }) => ({
            label: x.name,
            value: x.id,
            enabled: x.is_enabled,
        }));
    }

    async function searchUsers(val: string) {
        const params = {
            items_per_page: 1000,
            'order[last_name]': 'ASC',
            q: val,
        };
        const { results } = await getUsers(params);
        return results.map((x: UserType) => ({
            value: x.id,
            label: `${x.first_name} ${x.last_name} (${x.email})`,
        }));
    }

    React.useEffect(() => {
        if (selectedProvider !== null) {
            setPatientSelectorDisabled('patientIds');
        } else if (patientIds !== '') {
            setPatientSelectorDisabled('selectedProviders');
        } else {
            setPatientSelectorDisabled(null);
        }
    }, [selectedProvider, patientIds]);

    React.useEffect(() => {
        setValid(true);
        if (reportType === null) {
            setValid(false);
        }
        if (!isUserAccessReport) {
            if ([startDate, endDate].includes(null)) setValid(false);
            if (
                reportType?.label === 'Patient Activity' &&
                selectedProvider === null &&
                patientIds.trim() === ''
            )
                setValid(false);
            if (reportType?.label !== 'Patient Activity' && selectedUser === null) setValid(false);

            // validate date range
            if (startDate !== null && endDate !== null) {
                if (moment(endDate).isBefore(moment(startDate), 'day')) {
                    setInvalidDatesError('End Date must be after Start Date');
                    setValid(false);
                    return;
                }
                if (moment(endDate).isAfter(moment(), 'day')) {
                    setInvalidDatesError('End Date cannot be greater than today');
                    setValid(false);
                    return;
                }
                if (moment(startDate).isBefore(moment(endDate).subtract(2, 'years'), 'day')) {
                    setInvalidDatesError('End Date cannot exceed 2 years after Start Date');
                    setValid(false);
                    return;
                }
                setInvalidDatesError('');
            }
        }
    }, [
        reportType,
        selectedUser,
        patientIds,
        selectedProvider,
        endDate,
        startDate,
        isUserAccessReport,
    ]);

    const displayProviderOptions = (
        { enabled, label }: ReportProviderType,
        { context }: FormatOptionLabelMeta<ReportProviderType>
    ) => {
        if (context === 'menu' && !enabled) {
            return <span className="text-secondary fst-italic">{label} - Inactive</span>;
        }
        return label;
    };

    return (
        <>
            <button type="button" className="btn btn-primary" onClick={() => setShow(true)}>
                <i className="fa-solid fa-plus" /> New Report
            </button>
            <BSModal
                show={show}
                onHide={close}
                aria-labelledby="report-modal-title"
                id="report-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title id="report-modal-title" role="heading" aria-level={1}>
                        New Report
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form
                        id="create-report"
                        onSubmit={save}
                        disabled={saving}
                        values={{ reportType }}
                    >
                        <div className="row">
                            <FormGroup htmlFor="report-type" className="col-12">
                                <label htmlFor="report-type" className="col-form-label">
                                    Report Type
                                </label>
                                <Select
                                    components={{
                                        Option: OptionWithDescription,
                                    }}
                                    label="report-type"
                                    id="report-type"
                                    classNamePrefix="option-with-description"
                                    options={reportOptions}
                                    value={reportType}
                                    onChange={(val: ReportTypeType) => setReportType(val)}
                                />
                            </FormGroup>
                        </div>
                        {isUserAccessReport && (
                            <div className="row">
                                <FormGroup htmlFor="market-restriction" className="col-12">
                                    <label htmlFor="market-restriction" className="col-form-label">
                                        Market
                                    </label>
                                    <Select
                                        label="market-restriction"
                                        id="market-restriction"
                                        placeholder="All Markets"
                                        isMulti
                                        options={marketList}
                                        value={marketRestrictions}
                                        onChange={(val: ReportMarketType[]) =>
                                            setMarketRestrictions(val)
                                        }
                                    />
                                </FormGroup>
                            </div>
                        )}
                        {!isUserAccessReport && reportType?.value && (
                            <>
                                {reportType.label === 'Patient Activity' ? (
                                    <div className="row">
                                        <FormGroup htmlFor="provider-select" className="col-12">
                                            <label
                                                htmlFor="provider-select"
                                                className="col-form-label"
                                            >
                                                Provider
                                            </label>
                                            <Select
                                                label="provider-select"
                                                id="provider-select"
                                                key="provider-select"
                                                placeholder="Search Providers"
                                                async
                                                isClearable
                                                defaultOptions
                                                loadOptions={searchProviders}
                                                value={selectedProvider}
                                                onChange={setSelectedProvider}
                                                formatOptionLabel={displayProviderOptions}
                                                isDisabled={
                                                    patientSelectorDisabled === 'selectedProviders'
                                                }
                                            />
                                        </FormGroup>
                                        <FormGroup htmlFor="patient-ids" className="col-12">
                                            <label htmlFor="patient-ids" className="col-form-label">
                                                Patients
                                            </label>
                                            <input
                                                type="text"
                                                id="patient-ids"
                                                className="form-control"
                                                placeholder="Enter GMPIs separated by commas"
                                                value={patientIds}
                                                onChange={(e) => setPatientIds(e.target.value)}
                                                disabled={patientSelectorDisabled === 'patientIds'}
                                            />
                                        </FormGroup>
                                    </div>
                                ) : (
                                    <div className="row">
                                        <FormGroup htmlFor="user-select" className="col-12">
                                            <label htmlFor="user-select" className="col-form-label">
                                                User
                                            </label>
                                            <Select
                                                label="user-select"
                                                id="user-select"
                                                key="user-select"
                                                placeholder="Search User"
                                                async
                                                isClearable
                                                defaultOptions
                                                loadOptions={searchUsers}
                                                value={selectedUser}
                                                onChange={setSelectedUser}
                                            />
                                        </FormGroup>
                                    </div>
                                )}
                                <div className="row">
                                    <FormGroup htmlFor="start-date" className="col-6">
                                        <label htmlFor="start-date" className="col-form-label">
                                            Start Date
                                        </label>
                                        <div>
                                            <DatePicker
                                                id="start-date"
                                                name="start-date"
                                                value={startDate}
                                                onChange={(val: null | Date) => setStartDate(val)}
                                            />
                                        </div>
                                    </FormGroup>
                                    <FormGroup htmlFor="end-date" className="col-6">
                                        <label htmlFor="end-date" className="col-form-label">
                                            End Date
                                        </label>
                                        <div>
                                            <DatePicker
                                                id="end-date"
                                                name="end-date"
                                                value={endDate}
                                                onChange={(val: null | Date) => setEndDate(val)}
                                            />
                                        </div>
                                        <ValidationMessage
                                            type="external"
                                            rule={invalidDatesError !== ''}
                                            showBeforeSubmit
                                        >
                                            {invalidDatesError}
                                        </ValidationMessage>
                                    </FormGroup>
                                </div>
                            </>
                        )}
                        {validationErrors && (
                            <div className="mt-2 zp-form-validation-message">
                                {validationErrors}
                            </div>
                        )}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-secondary" onClick={close}>
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={saving || !valid}
                        form="create-report"
                    >
                        {saving && (
                            <Spinner animation="border" size="sm" role="status" className="me-2" />
                        )}
                        Submit
                    </button>
                </Modal.Footer>
            </BSModal>
        </>
    );
}
